import React, { useState } from 'react';
import { VolunteerExperience } from '../../../types/profile';
import { EditableSection } from '../common/EditableSection';
import { FormInput } from '../common/FormInput';
import { ListInput } from '../common/ListInput';

interface VolunteerExperienceSectionProps {
  experiences: VolunteerExperience[];
  onUpdate: (experiences: VolunteerExperience[]) => void;
}

interface VolunteerFormProps {
  experience: VolunteerExperience;
  onChange: (experience: VolunteerExperience) => void;
  onRemove: () => void;
}

const VolunteerForm: React.FC<VolunteerFormProps> = ({ experience, onChange, onRemove }) => {
  const handleFieldChange = (field: keyof VolunteerExperience) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    onChange({ ...experience, [field]: e.target.value });
  };

  const handleListChange = (items: string[]) => {
    onChange({ ...experience, responsibilities: items });
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 space-y-4">
      <div className="flex justify-between items-start">
        <h4 className="text-lg font-medium text-gray-900">Volunteer Entry</h4>
        <button
          type="button"
          onClick={onRemove}
          className="text-red-600 hover:text-red-800"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormInput
          label="Organization"
          name="organization"
          value={experience.organization}
          onChange={handleFieldChange('organization')}
          maxLength={50}
        />
        <FormInput
          label="Job Title"
          name="title"
          value={experience.title}
          onChange={handleFieldChange('title')}
          maxLength={100}
        />
        <FormInput
          label="Location"
          name="location"
          value={experience.location}
          onChange={handleFieldChange('location')}
          maxLength={50}
        />
        <div>
          <label className="block text-sm font-medium text-gray-700">From</label>
          <input
            type="month"
            value={experience.from}
            onChange={handleFieldChange('from')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">To</label>
          <input
            type="month"
            value={experience.to}
            onChange={handleFieldChange('to')}
            placeholder="Present"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
        </div>
        <div className="md:col-span-2">
          <ListInput
            label="Responsibilities"
            items={experience.responsibilities}
            onItemsChange={handleListChange}
            placeholder="Add responsibility"
          />
        </div>
      </div>
    </div>
  );
};

export const VolunteerExperienceSection: React.FC<VolunteerExperienceSectionProps> = ({
  experiences,
  onUpdate
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editExperiences, setEditExperiences] = useState<VolunteerExperience[]>(experiences);

  const handleEdit = () => {
    setEditExperiences(experiences);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditExperiences(experiences);
    setIsEditing(false);
  };

  const handleSave = () => {
    const sortedExperiences = [...editExperiences].sort((a, b) => {
      const dateA = a.from || '0000-00';
      const dateB = b.from || '0000-00';
      return dateB.localeCompare(dateA);
    });
    onUpdate(sortedExperiences);
    setIsEditing(false);
  };

  const handleAddExperience = () => {
    setEditExperiences([
      ...editExperiences,
      {
        organization: '',
        title: '',
        location: '',
        from: '',
        to: '',
        responsibilities: []
      }
    ]);
  };

  const handleUpdateExperience = (index: number) => (experience: VolunteerExperience) => {
    const updated = [...editExperiences];
    updated[index] = experience;
    setEditExperiences(updated);
  };

  const handleRemoveExperience = (index: number) => () => {
    setEditExperiences(editExperiences.filter((_, i) => i !== index));
  };

  const formatDate = (date: string) => {
    if (!date) return 'Present';
    if (date.toLowerCase().includes('present')) return 'Present';
    const [year, month] = date.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  const sortedViewExperiences = [...experiences].sort((a, b) => {
    const dateA = a.from || '0000-00';
    const dateB = b.from || '0000-00';
    return dateB.localeCompare(dateA);
  });

  return (
    <EditableSection
      title="Volunteer Experience"
      isEditing={isEditing}
      onEdit={handleEdit}
      onSave={handleSave}
      onCancel={handleCancel}
    >
      {isEditing ? (
        <div className="space-y-4">
          {editExperiences.map((exp, index) => (
            <VolunteerForm
              key={index}
              experience={exp}
              onChange={handleUpdateExperience(index)}
              onRemove={handleRemoveExperience(index)}
            />
          ))}
          <button
            type="button"
            onClick={handleAddExperience}
            className="w-full py-2 px-4 border-2 border-dashed border-gray-300 rounded-md text-sm font-medium text-gray-600 hover:border-gray-400 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            + Add Volunteer Experience
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {sortedViewExperiences.length > 0 ? (
            sortedViewExperiences.map((exp, index) => (
              <div key={index} className="border-b border-gray-200 pb-6 last:border-0">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{exp.title}</h3>
                    <p className="text-sm text-gray-600">{exp.organization} • {exp.location}</p>
                  </div>
                  <p className="text-sm text-gray-500">
                    {formatDate(exp.from)} - {formatDate(exp.to)}
                  </p>
                </div>
                {exp.responsibilities.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm font-medium text-gray-700 mb-1">Responsibilities:</p>
                    <ul className="list-disc list-inside space-y-1">
                      {exp.responsibilities.map((resp, idx) => (
                        <li key={idx} className="text-sm text-gray-600">{resp}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))
          ) : (
            <p className="text-gray-500 italic">No volunteer experience added yet.</p>
          )}
        </div>
      )}
    </EditableSection>
  );
};